import React, { useEffect, useState } from 'react';
import { FaCopy } from 'react-icons/fa';
import Loader from '../../UI/Loader/Loader';
import { useAppDispatch } from '../../../store/hooks';
import { customAlert } from '../../../store/actions/alert';
import get from '../../../services/axios/get';
import Load from '../../../models/load';
import { Document } from '../Documents/Documents';
import styles from './PreAdviseDetails.module.scss';
import Button from '../../UI/Button/Button';

interface PreAdviseDetailsProps {
  load: Load;
  close: () => void;
}

const PreAdviseDetails: React.FC<PreAdviseDetailsProps> = ({ load, close }) => {
  const dispatch = useAppDispatch();
  const [preAdviseDocuments, setPreAdviseDocuments] = useState<Record<string, Document | null>>({
    'Container Photo': null,
    'Seal Photo': null,
    'Booking Photo': null,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!load?.id) return;
    setIsLoading(true);
    get(
      '/document',
      { loadId: load.id },
      (res) => {
        const map: Record<string, Document | null> = {
          'Container Photo': null,
          'Seal Photo': null,
          'Booking Photo': null,
        };
        res.data?.forEach((doc: Document) => {
          const name = (doc.documentName || '').split('.')[0];
          if (map[name] !== undefined) map[name] = doc;
        });
        setPreAdviseDocuments(map);
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
  }, [load?.id]);

  const copyToClipboard = (value: string | undefined) => {
    if (!value) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(() => { });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); } catch { }
      document.body.removeChild(textarea);
    }
    dispatch(customAlert(true, 'Copied'));
    setTimeout(() => dispatch(customAlert(false, '')), 1500);
  };

  const totalVgm = (
    (parseFloat(load.vgmLoadpoint1 || '0') || 0) +
    (parseFloat(load.vgmLoadpoint2 || '0') || 0) +
    (parseFloat(load.containerTare || '0') || 0)
  ).toFixed(0);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h5>Pre-advise details</h5>
      </div>

      {isLoading ? (
        <div className={styles.loadingWrapper}>
          <Loader />
        </div>
      ) : (
        <div className={styles.contentGrid}>
          <div className={styles.photoGrid}>
            <div>
              <div className={styles.sectionTitle}>Container photo</div>
              {preAdviseDocuments['Container Photo']?.signedUrl ? (
                <img
                  src={preAdviseDocuments['Container Photo']!.signedUrl}
                  alt="Container Photo"
                  className={styles.photo}
                />
              ) : (
                <div className={styles.noPhoto}>No container photo</div>
              )}
            </div>
            <div>
              <div className={styles.sectionTitle}>Seal photo</div>
              {preAdviseDocuments['Seal Photo']?.signedUrl ? (
                <img
                  src={preAdviseDocuments['Seal Photo']!.signedUrl}
                  alt="Seal Photo"
                  className={styles.photo}
                />
              ) : (
                <div className={styles.noPhoto}>No seal photo</div>
              )}
            </div>
          </div>

          <div className={styles.infoGrid}>
            <div className={styles.infoCard}>
              <div>
                <div className={styles.label}>Container No</div>
                <div className={styles.value}>{load.containerNumber || '-'}</div>
              </div>
              <button
                onClick={() => copyToClipboard(load.containerNumber || '')}
                className="btn btn-light btn-sm"
              >
                <FaCopy />
              </button>
            </div>
            <div className={styles.infoCard}>
              <div>
                <div className={styles.label}>Seal No</div>
                <div className={styles.value}>{load.sealNumber || '-'}</div>
              </div>
              <button
                onClick={() => copyToClipboard(load.sealNumber || '')}
                className="btn btn-light btn-sm"
              >
                <FaCopy />
              </button>
            </div>
          </div>

          <div className={styles.infoGrid}>
            <div className={styles.vgmCard}>
              <div className={styles.vgmHeader}>
                <div className={styles.vgmTitle}>VGM: {totalVgm} kg</div>
                <button
                  onClick={() => copyToClipboard(totalVgm)}
                  className="btn btn-light btn-sm"
                >
                  <FaCopy />
                </button>
              </div>
              <div className={styles.metricsGrid}>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>Container tare</div>
                  <div className={styles.metricValueRow}>
                    <span>{load.containerTare || '-'}</span>
                    <button onClick={() => copyToClipboard(load.containerTare || '')} className="btn btn-light btn-sm"><FaCopy /></button>
                  </div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>Load weight (LP1)</div>
                  <div className={styles.metricValueRow}>
                    <span>{load.vgmLoadpoint1 || '-'}</span>
                    <button onClick={() => copyToClipboard(load.vgmLoadpoint1 || '')} className="btn btn-light btn-sm"><FaCopy /></button>
                  </div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>Load weight (LP2)</div>
                  <div className={styles.metricValueRow}>
                    <span>{load.vgmLoadpoint2 || '-'}</span>
                    <button onClick={() => copyToClipboard(load.vgmLoadpoint2 || '')} className="btn btn-light btn-sm"><FaCopy /></button>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.infoCard}>
              <div>
                <div className={styles.label}>Booking Ref</div>
                <div className={styles.value}>{load.bookingRef || '-'}</div>
              </div>
              <button
                onClick={() => copyToClipboard((load.bookingRef || ''))}
                className="btn btn-light btn-sm"
              >
                <FaCopy />
              </button>
            </div>
          </div>

          <div className={styles.closeButton}>
            <Button
              type='button'
              buttonStyle='main'
              text='Close'
              click={close}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PreAdviseDetails;

